import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export default function UpdateSubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);

  useEffect(() => {
    // Get session parameters from URL (Update.dev typically passes these)
    const sessionId = searchParams.get('session_id');
    const subscriptionId = searchParams.get('subscription_id');
    
    // Simulate verification (you can replace this with actual verification logic)
    const verifySubscription = async () => {
      try {
        // Simulate API call to verify subscription
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Mock successful verification
        setSubscriptionDetails({
          subscriptionId: subscriptionId || 'sub_mock_123',
          planName: 'Professional Plan',
          amount: '$99/month',
          nextBilling: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        });
        
        setVerificationStatus('success');
      } catch (error) {
        console.error('Subscription verification failed:', error);
        setVerificationStatus('error');
      }
    };

    verifySubscription();
  }, [searchParams]);

  const handleContinue = () => {
    navigate('/admin/update-subscription-center');
  };

  const handleGoToDashboard = () => {
    navigate('/admin');
  };

  if (verificationStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <h2 className="text-lg font-medium">Verifying Your Subscription</h2>
              <p className="text-sm text-muted-foreground text-center">
                Please wait while we confirm your payment and set up your subscription...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Verification Failed</AlertTitle>
              <AlertDescription>
                We couldn't verify your subscription. Please contact support if this issue persists.
              </AlertDescription>
            </Alert>
            <div className="mt-6 flex gap-3">
              <Button variant="outline" onClick={() => navigate('/admin')} className="flex-1">
                Go to Dashboard
              </Button>
              <Button onClick={handleContinue} className="flex-1">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-green-900">
            Subscription Activated!
          </CardTitle>
          <CardDescription>
            Welcome to your Hivetivity platform subscription
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border p-4 space-y-3">
            <h3 className="font-medium">Subscription Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan:</span>
                <span className="font-medium">{subscriptionDetails?.planName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount:</span>
                <span className="font-medium">{subscriptionDetails?.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Next Billing:</span>
                <span className="font-medium">{subscriptionDetails?.nextBilling}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subscription ID:</span>
                <span className="font-mono text-xs">{subscriptionDetails?.subscriptionId}</span>
              </div>
            </div>
          </div>

          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertTitle>You're all set!</AlertTitle>
            <AlertDescription>
              Your organization now has full access to the Hivetivity platform. You can manage your subscription anytime from the subscription center.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3">
            <Button variant="outline" onClick={handleGoToDashboard} className="flex-1">
              Go to Dashboard
            </Button>
            <Button onClick={handleContinue} className="flex-1">
              Manage Subscription
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 